import { createContext, useContext, useState, useEffect, useMemo } from 'react'
import { themes, DEFAULT_THEME, THEME_STORAGE_KEY } from '../constants/themes.js'

const ThemeContext = createContext(null)

function getSavedTheme() {
  try {
    const saved = localStorage.getItem(THEME_STORAGE_KEY)
    if (saved && themes[saved]) return saved
  } catch {
    // localStorage unavailable
  }
  return DEFAULT_THEME
}

function applyTheme(themeKey) {
  const theme = themes[themeKey]
  if (!theme) return
  const style = document.documentElement.style
  for (const [prop, value] of Object.entries(theme.variables)) {
    style.setProperty(prop, value)
  }
}

export function ThemeProvider({ children }) {
  const [currentTheme, setCurrentTheme] = useState(getSavedTheme)

  useEffect(() => {
    applyTheme(currentTheme)
    try {
      localStorage.setItem(THEME_STORAGE_KEY, currentTheme)
    } catch {
      // localStorage unavailable
    }
  }, [currentTheme])

  const setTheme = (themeKey) => {
    if (themes[themeKey]) setCurrentTheme(themeKey)
  }

  const value = useMemo(() => ({
    currentTheme,
    themeName: themes[currentTheme]?.name ?? 'Unknown',
    setTheme,
    availableThemes: Object.entries(themes).map(([id, t]) => ({ id, name: t.name })),
  }), [currentTheme])

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme() {
  const ctx = useContext(ThemeContext)
  if (!ctx) throw new Error('useTheme must be used within a ThemeProvider')
  return ctx
}
